import React, {useState} from "react";
import {Form, Modal} from "antd";
import {useForm} from "antd/es/form/Form";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {HealthCheckerCheck} from "../../../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../../../services/health-checker/health-checker.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {UIInputWidgetHealthChecker} from "../../../../property/ui/input/widget/health-checker/ui.input.widget.health-checker";
import {UIInputWidgetHealthCheckerGroup} from "../../../../property/ui/input/widget/health-checker-group/ui.input.widget.health-checker-group";
import {
    healthCheckerCheckFormToRequest,
    healthCheckerCheckToFormValues,
} from "./checks.helpers";
import {HealthCheckerCheckFormValues} from "./checks.types";
import {Trans} from "react-i18next";
import {UIInputWidgetInputTags} from "../../../../common/ui/input/widget/input-tags/ui.input.widget.input-tags";

interface HealthCheckerCheckModalProps {
    check: HealthCheckerCheck | null;
    onCancel: () => void;
    onComplete: () => Promise<void> | void;
}

export const HealthCheckerCheckModal: React.FC<HealthCheckerCheckModalProps> = ({
    check,
    onCancel,
    onComplete,
}) => {
    const t = useTranslate();
    const [form] = useForm<HealthCheckerCheckFormValues>();
    const [loading, setLoading] = useState<boolean>(false);

    const submit = async (values: HealthCheckerCheckFormValues) => {
        const request = healthCheckerCheckFormToRequest(values);
        let completed = false;

        try {
            setLoading(true);
            if (check) {
                await healthCheckerService.updateCheck(check.id, request);
            } else {
                await healthCheckerService.createCheck(request);
            }
            await onComplete();
            notifications.successfully();
            completed = true;
        } catch (error: any) {
            form.setFields(error.response?.data?.fields || []);
        } finally {
            setLoading(false);
        }

        if (completed) {
            onCancel();
        }
    };

    return (
        <Modal
            className="health-center-checks-modal"
            title={t(
                check
                    ? "HEALTH-CENTER.SETTINGS.CHECKS.EDIT"
                    : "HEALTH-CENTER.SETTINGS.CHECKS.CREATE",
                {defaultValue: check ? "Edit check" : "Create check"}
            )}
            width={560}
            open
            destroyOnClose
            confirmLoading={loading}
            cancelButtonProps={{disabled: loading}}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form<HealthCheckerCheckFormValues>
                form={form}
                layout="vertical"
                autoComplete="off"
                disabled={loading}
                initialValues={healthCheckerCheckToFormValues(check)}
                onFinish={submit}
            >
                <Form.Item
                    label={t("HEALTH-CENTER.SETTINGS.CHECKS.CHECKER-NAME", {defaultValue: "Checker"})}
                    name="checkerName"
                    rules={[{
                        required: true,
                        message: t("FROM.ERROR.PLEASE_SELECT_VALUE", {defaultValue: "Please select a value"}),
                    }]}
                >
                    <UIInputWidgetHealthChecker loading={loading} />
                </Form.Item>

                <Form.Item
                    label={t("HEALTH-CENTER.SETTINGS.CHECKS.GROUP", {defaultValue: "Group"})}
                    name="group"
                    rules={[{
                        required: true,
                        message: t("FROM.ERROR.PLEASE_SELECT_VALUE", {defaultValue: "Please select a value"}),
                    }]}
                >
                    <UIInputWidgetHealthCheckerGroup loading={loading} />
                </Form.Item>

                <Form.Item
                    name="hours"
                    label={"Hours"}
                    rules={[{ required: true, message: t("FROM.ERROR.PLEASE_ENTER_VALUE") }]}
                >
                    <UIInputWidgetInputTags />
                </Form.Item>
            </Form>
        </Modal>
    );
};
